import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";

function tag(data, hexTag) {
  try { return (data.string(hexTag) || "").trim(); } catch { return ""; }
}

function extractOverlay(image) {
  const d = image.data;
  const patientName  = tag(d, "x00100010").replace(/\^/g, " ");
  const patientId    = tag(d, "x00100020");
  const patientSex   = tag(d, "x00100040");
  const studyDate    = tag(d, "x00080020");
  const studyTime    = tag(d, "x00080030");
  const modality     = tag(d, "x00080060");
  const institution  = tag(d, "x00080080");
  const manufacturer = tag(d, "x00080070");
  const stationName  = tag(d, "x00081010");
  const seriesDesc   = tag(d, "x0008103e");
  const viewPosition = tag(d, "x00185101");

  let dateStr = studyDate;
  if (studyDate && studyDate.length === 8) {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    dateStr = `${studyDate.slice(6,8)}-${months[parseInt(studyDate.slice(4,6),10)-1]||"?"}-${studyDate.slice(0,4)}`;
  }
  let timeStr = studyTime;
  if (studyTime && studyTime.length >= 6) {
    timeStr = `${studyTime.slice(0,2)}:${studyTime.slice(2,4)}:${studyTime.slice(4,6)}`;
  }

  return {
    topLeft:     [patientName, patientId ? `ID: ${patientId}` : "", patientSex ? `Sex: ${patientSex}` : ""].filter(Boolean),
    topRight:    [institution, manufacturer || stationName, modality].filter(Boolean),
    bottomLeft:  [(seriesDesc || viewPosition), image.rows && image.columns ? `${image.columns}x${image.rows} px` : "", dateStr ? `${dateStr}${timeStr ? "  " + timeStr : ""}` : ""].filter(Boolean),
    bottomRight: [`WC: ${Math.round(image.windowCenter ?? 0)}`, `WW: ${Math.round(image.windowWidth ?? 0)}`],
  };
}

function computeDims(columns, rows) {
  const MAX_W = 800, MAX_H = 700;
  if (!columns || !rows) return { w: MAX_W, h: MAX_H };
  const scale = Math.min(MAX_W / columns, MAX_H / rows);
  return { w: Math.round(columns * scale), h: Math.round(rows * scale) };
}

const DicomViewer = forwardRef(function DicomViewer({ fileUrl, zoom = 1 }, ref) {
  const elRef       = useRef(null);
  const csRef       = useRef(null);
  const imageRef    = useRef(null);
  const fitScaleRef = useRef(null);   // fit-to-container scale (zoom baseline)
  const voiRef      = useRef(null);   // { windowWidth, windowCenter } — persisted across zoom
  const enabledRef  = useRef(false);

  const [status,  setStatus]  = useState("Starting...");
  const [error,   setError]   = useState(null);
  const [dims,    setDims]    = useState(null);
  const [loaded,  setLoaded]  = useState(false);
  const [overlay, setOverlay] = useState(null);

  useImperativeHandle(ref, () => ({
    openInNewTab: () => {
      try {
        const canvas = elRef.current?.querySelector("canvas");
        if (!canvas) return;
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const win = window.open(url, "_blank");
          if (win) win.focus();
        }, "image/png");
      } catch {}
    }
  }));

  // Phase 1: load image, compute dims
  useEffect(() => {
    let cancelled = false;
    enabledRef.current = false;
    fitScaleRef.current = null;
    voiRef.current = null;
    csRef.current = null;
    imageRef.current = null;
    setDims(null);
    setLoaded(false);
    setError(null);
    setOverlay(null);
    setStatus("Loading cornerstone-core...");

    (async () => {
      try {
        const csModule = await import("cornerstone-core");
        const cs = csModule.default || csModule;
        if (!cs?.enable) throw new Error("cornerstone-core missing 'enable'.");
        csRef.current = cs;

        setStatus("Loading image loader...");
        const loaderModule = await import("cornerstone-wado-image-loader");
        const loader = loaderModule.default || loaderModule;
        if (!loader?.external) throw new Error("wado-image-loader missing 'external'.");

        setStatus("Loading dicom-parser...");
        const parserModule = await import("dicom-parser");
        const dicomParser  = parserModule.default || parserModule;

        loader.external.cornerstone = cs;
        loader.external.dicomParser = dicomParser;
        loader.webWorkerManager.initialize({
          maxWebWorkers: 2,
          startWebWorkersOnDemand: true,
          taskConfiguration: { decodeTask: { initializeCodecsOnStartup: false } },
        });

        setStatus("Fetching DICOM file...");
        const image = await cs.loadAndCacheImage("wadouri:" + fileUrl);
        if (cancelled) return;
        imageRef.current = image;
        setDims(computeDims(image.columns, image.rows));
        setStatus("Rendering...");
      } catch (e) {
        if (!cancelled) {
          // Cornerstone throws raw XHR objects — extract status code if available
          const xhr = e instanceof XMLHttpRequest ? e : (e?.xhr || e?.request);
          if (xhr && xhr.status === 401) setError("401 — Not authorized. The file link may have expired.");
          else if (xhr && xhr.status) setError(`HTTP ${xhr.status} — Could not load imaging file.`);
          else setError(typeof e === "string" ? e : (e?.message || "Failed to load DICOM file. The file may be unavailable."));
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (csRef.current && elRef.current && enabledRef.current) {
          csRef.current.disable(elRef.current);
        }
      } catch {}
      enabledRef.current = false;
    };
  }, [fileUrl]);

  // Phase 2: enable + render — fires after dims applied to DOM
  useEffect(() => {
    if (!dims || !imageRef.current || !csRef.current || !elRef.current) return;
    const cs      = csRef.current;
    const element = elRef.current;
    const image   = imageRef.current;

    try {
      cs.enable(element);
      enabledRef.current = true;
      cs.resize(element, true);
      cs.displayImage(element, image);

      // Compute and store VOI (windowing) values in ref so zoom never loses them
      let ww = image.windowWidth;
      let wc = image.windowCenter;
      if (!(ww > 0 && typeof wc === "number")) {
        if (typeof image.minPixelValue === "number" && typeof image.maxPixelValue === "number") {
          ww = Math.max(image.maxPixelValue - image.minPixelValue, 1);
          wc = image.minPixelValue + ww / 2;
        }
      }
      voiRef.current = { ww, wc };

      // Compute fit-to-container scale and store as zoom baseline
      const scaleX   = element.clientWidth  / (image.columns || element.clientWidth);
      const scaleY   = element.clientHeight / (image.rows    || element.clientHeight);
      fitScaleRef.current = Math.min(scaleX, scaleY);

      // Apply everything to the viewport
      const vp = cs.getViewport(element);
      vp.voi.windowWidth  = ww;
      vp.voi.windowCenter = wc;
      vp.scale            = fitScaleRef.current;
      vp.translation      = { x: 0, y: 0 };
      cs.setViewport(element, vp);

      try { setOverlay(extractOverlay(image)); } catch {}
      setLoaded(true);
      setStatus("Done");
    } catch (e) {
      const xhr = e instanceof XMLHttpRequest ? e : (e?.xhr || e?.request);
      if (xhr && xhr.status === 401) setError("401 — Not authorized. The file link may have expired.");
      else if (xhr && xhr.status) setError(`HTTP ${xhr.status} — Could not load imaging file.`);
      else setError(typeof e === "string" ? e : (e?.message || "Failed to load DICOM file."));
    }
  }, [dims]);

  // Zoom — always reapply stored VOI so windowing is never lost
  useEffect(() => {
    if (!loaded || !csRef.current || !elRef.current) return;
    if (fitScaleRef.current === null || voiRef.current === null) return;
    try {
      const vp = csRef.current.getViewport(elRef.current);
      if (!vp) return;
      // Always set both scale AND voi together so zoom never corrupts windowing
      vp.scale            = fitScaleRef.current * zoom;
      vp.translation      = { x: 0, y: 0 };
      vp.voi.windowWidth  = voiRef.current.ww;
      vp.voi.windowCenter = voiRef.current.wc;
      csRef.current.setViewport(elRef.current, vp);
    } catch {}
  }, [zoom, loaded]);

  const containerW = dims?.w ?? 640;
  const containerH = dims?.h ?? 480;

  const txt = {
    fontSize: 11, fontFamily: "monospace", color: "#fff",
    textShadow: "1px 1px 2px #000, -1px -1px 2px #000",
    lineHeight: "1.6", whiteSpace: "nowrap", pointerEvents: "none",
  };

  return (
    <div style={{ position: "relative", width: containerW, height: containerH }}>
      {!loaded && !error && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 13, textAlign: "center", background: "#000", zIndex: 2 }}>
          {status}
        </div>
      )}
      {error && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#000", zIndex: 2 }}>
          <div style={{ color: "#FCA5A5", fontSize: 13, padding: 24, textAlign: "center", maxWidth: 560 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Could not load DICOM file</div>
            <div style={{ fontFamily: "monospace", fontSize: 12, background: "#1E293B", padding: 12, borderRadius: 6, wordBreak: "break-word" }}>{error}</div>
          </div>
        </div>
      )}
      <div ref={elRef} style={{ width: containerW, height: containerH, background: "#000" }} />
      {loaded && overlay && (
        <>
          <div style={{ position: "absolute", top: 8, left: 10, zIndex: 3 }}>
            {overlay.topLeft.map((l, i) => <div key={i} style={txt}>{l}</div>)}
          </div>
          <div style={{ position: "absolute", top: 8, right: 10, zIndex: 3, textAlign: "right" }}>
            {overlay.topRight.map((l, i) => <div key={i} style={txt}>{l}</div>)}
          </div>
          <div style={{ position: "absolute", bottom: 8, left: 10, zIndex: 3 }}>
            {overlay.bottomLeft.map((l, i) => <div key={i} style={txt}>{l}</div>)}
          </div>
          <div style={{ position: "absolute", bottom: 8, right: 10, zIndex: 3, textAlign: "right" }}>
            {overlay.bottomRight.map((l, i) => <div key={i} style={txt}>{l}</div>)}
          </div>
        </>
      )}
    </div>
  );
});

export default DicomViewer;